import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { MapPin, Phone, Mail, Calendar, ArrowRight, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

const Contact = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    industry: '',
    message: '',
    website_url: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const { data, error } = await supabase.functions.invoke('submit-lead', {
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          industry: formData.industry,
          message: formData.message,
          modules: [], // Generic contact has no modules
          website_url: formData.website_url
        }
      });

      if (error) {
        throw new Error(error.message || 'Error submitting contact request');
      }

      setIsSubmitted(true);
    } catch (err: any) {
      console.error('Contact form submission failed:', err);
      setSubmitError(
        language === 'en'
          ? 'There was a problem sending your message. Please check your connection and try again.'
          : 'Hubo un problema al enviar tu mensaje. Por favor verifica tu conexión e intenta de nuevo.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t.contact.title}
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              {t.contact.subtitle}
            </p>

            <div className="space-y-6 mb-10">
              <div className="flex items-start">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mr-4" style={{ backgroundColor: '#e0f2fe' }}>
                  <MapPin className="w-6 h-6" style={{ color: '#115d7b' }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {t.contact.office}
                  </h3>
                  <p className="text-gray-600">
                    {t.contact.address.line1}<br />
                    {t.contact.address.line2}<br />
                    {t.contact.address.city}, {t.contact.address.state} {t.contact.address.zip}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mr-4" style={{ backgroundColor: '#e0f2fe' }}>
                  <Phone className="w-6 h-6" style={{ color: '#115d7b' }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {t.contact.phone}
                  </h3>
                  <p className="text-gray-600">
                    <a href="tel:+528111071450" className="transition-colors" style={{ color: '#115d7b' }}>
                      +52 81 1107 1450
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mr-4" style={{ backgroundColor: '#e0f2fe' }}>
                  <Mail className="w-6 h-6" style={{ color: '#115d7b' }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {t.contact.email}
                  </h3>
                  <p className="text-gray-600">
                    <a href="mailto:info@laimu.mx" className="transition-colors" style={{ color: '#115d7b' }}>
                      info@laimu.mx
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-start mb-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mr-3" style={{ backgroundColor: '#fff7ed' }}>
                  <Calendar className="w-5 h-5" style={{ color: '#ff8c00' }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t.contact.scheduleDemo}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {t.contact.scheduleDemoText}
                  </p>
                </div>
              </div>
              <a 
                href="/demo" 
                className="w-full px-5 py-3 text-white rounded-md transition-colors text-center block font-medium flex items-center justify-center"
                style={{ backgroundColor: '#ff8c00' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#e67e00'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#ff8c00'}
              >
                {t.contact.scheduleButton}
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-md">
            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {language === 'en' ? 'Message Sent!' : '¡Mensaje Enviado!'}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed text-sm md:text-base">
                  {language === 'en'
                    ? 'Thank you for contacting us. Our team will review your message and get back to you shortly.'
                    : 'Gracias por ponerte en contacto con nosotros. Nuestro equipo revisará tu mensaje y te responderá a la brevedad.'
                  }
                </p>
                <button
                  onClick={() => {
                    setIsSubmitted(false);
                    setFormData({
                      name: '',
                      email: '',
                      company: '',
                      phone: '',
                      industry: '',
                      message: '',
                      website_url: ''
                    });
                  }}
                  className="px-6 py-3 text-white rounded-md transition-colors font-medium"
                  style={{ backgroundColor: '#115d7b' }}
                >
                  {language === 'en' ? 'Send Another Message' : 'Enviar Otro Mensaje'}
                </button>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {t.contact.formTitle}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {language === 'en'
                      ? 'Fill out the form and we will contact you within 24 hours'
                      : 'Completa el formulario y te contactaremos en 24 horas'
                    }
                  </p>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        {t.contact.form.name} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                        style={{ '--tw-ring-color': '#ff8c00' } as React.CSSProperties}
                        placeholder={t.contact.form.namePlaceholder}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        {language === 'en' ? 'Corporate Email' : 'Correo Corporativo'} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                        style={{ '--tw-ring-color': '#ff8c00' } as React.CSSProperties}
                        placeholder={language === 'en' ? 'you@company.com' : 'tu@empresa.com'}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        {t.contact.form.company} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="company"
                        required
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                        style={{ '--tw-ring-color': '#ff8c00' } as React.CSSProperties}
                        placeholder={t.contact.form.companyPlaceholder}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        {t.contact.form.phone || (language === 'en' ? 'Phone' : 'Teléfono')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                        style={{ '--tw-ring-color': '#ff8c00' } as React.CSSProperties}
                        placeholder="+52 81 1234 5678"
                      />
                    </div>
                  </div>

                  <div>
                    <details className="group">
                      <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors flex items-center">
                        <span className="mr-2">{language === 'en' ? 'Additional information (optional)' : 'Información adicional (opcional)'}</span>
                        <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </summary>
                      <div className="mt-4 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.contact.form.industry}
                          </label>
                          <select
                            name="industry"
                            value={formData.industry}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                            style={{ '--tw-ring-color': '#ff8c00' } as React.CSSProperties}
                          >
                            <option value="">{t.contact.form.industryPlaceholder}</option>
                            {Object.entries(t.industries).map(([key, value]) => (
                              <option key={key} value={key}>
                                {value.title}
                              </option>
                            ))}
                            <option value="other">{t.contact.form.other}</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.contact.form.message}
                          </label>
                          <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent min-h-[100px] transition-all"
                            style={{ '--tw-ring-color': '#ff8c00' } as React.CSSProperties}
                            placeholder={t.contact.form.messagePlaceholder}
                          />
                        </div>
                      </div>
                    </details>
                  </div>

                  {/* Honeypot field - visually hidden */}
                  <div className="absolute opacity-0 -z-10 select-none pointer-events-none w-0 h-0 overflow-hidden" aria-hidden="true">
                    <label htmlFor="website_url_contact">Website URL</label>
                    <input
                      id="website_url_contact"
                      type="text"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleInputChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  {submitError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-md animate-pulse">
                      {submitError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full px-5 py-4 text-white rounded-lg transition-all font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center ${
                      isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                    style={{ backgroundColor: '#ff8c00' }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting) {
                        e.currentTarget.style.backgroundColor = '#e67e00';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubmitting) {
                        e.currentTarget.style.backgroundColor = '#ff8c00';
                      }
                    }}
                  >
                    {isSubmitting 
                      ? (language === 'en' ? 'Sending Message...' : 'Enviando Mensaje...') 
                      : t.contact.form.submit}
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-3">
                    {language === 'en'
                      ? 'We respect your privacy. Your information is secure.'
                      : 'Respetamos tu privacidad. Tu información está segura.'
                    }
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;